/**
 * CORE-1517 — the way out of the app-level «Что-то пошло не так» screen.
 *
 * Background: on web a lazily loaded screen can fail to load its chunk (the
 * tab predates a deploy, a flaky CDN edge, Safari refusing the module). The
 * rejection trips the root ErrorBoundary. «Попробовать снова» re-renders the
 * same tree and asks the same broken chunk again — if that keeps failing the
 * person is stuck, with nothing but the raw `Loading module …/LivesScreen-…js
 * failed` text for company (Safari on DEV, screenshot from 28.09.2026).
 *
 * `goHomeFromErrorScreen` is a FULL document navigation to `/`, on purpose:
 *  - it does not depend on the React tree that just crashed, nor on
 *    react-navigation (the boundary sits above NavigationContainer);
 *  - a fresh `index.html` brings a fresh chunk manifest, so a stale chunk hash
 *    stops being a problem — the person lands on a working home without
 *    reloading the tab by hand.
 * It is user-initiated only. Nothing here reloads on its own, so an эфир is
 * never interrupted (CORE-714 holdback stays intact).
 *
 * Web only by construction: the caller passes `window.location` from inside a
 * `Platform.OS === 'web'` branch (I-2), tests pass a stub.
 */
export const HOME_PATH = '/';

export function goHomeFromErrorScreen(location: Pick<Location, 'assign'> | undefined): void {
    if (!location) return;
    try {
        location.assign(HOME_PATH);
    } catch {
        // A refused navigation must not throw out of the error screen itself.
    }
}

/**
 * What the fallback shows under the headline. Developers keep the raw message
 * (capped — a stack-trace-sized string is useless on screen); people on a
 * release build get a calm hint instead of a chunk URL. The full error still
 * reaches Sentry from componentDidCatch.
 */
export function resolveErrorDescription(error: Error | null, isDev: boolean): string | undefined {
    if (isDev) return error?.message?.slice(0, 200) || undefined;
    return 'Попробуйте ещё раз или вернитесь на главную.';
}

export type ErrorFallbackInput = {
    error: Error | null;
    /** Definitive connectivity signal (navigator.onLine / NetInfo). */
    online: boolean;
    isWeb: boolean;
    isDev: boolean;
};

export type ErrorFallbackCopy = {
    message: string;
    description?: string;
    actionLabel?: string;
    secondaryActionLabel?: string;
};

/**
 * What the app-level error screen says and offers, by situation.
 *
 * Offline is named as such: the person turned the network off (or the train
 * went into a tunnel), and a chunk failing to load is the symptom, not the
 * problem. Neither button is offered — a retry would fail again, and «/» as a
 * full document load would only land on the browser's own offline page. The
 * fallback retries by itself the moment connectivity comes back.
 */
export function errorFallbackProps({ error, online, isWeb, isDev }: ErrorFallbackInput): ErrorFallbackCopy {
    if (!online) {
        return {
            message: 'Нет соединения с интернетом',
            description: 'Когда сеть вернётся, попробуем снова сами.',
        };
    }
    return {
        message: 'Что-то пошло не так',
        description: resolveErrorDescription(error, isDev),
        actionLabel: 'Попробовать снова',
        secondaryActionLabel: isWeb ? 'На главную' : undefined,
    };
}
